import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { CalculatorWidget } from '@/types/widget';
import { EmbeddableCalculator } from '@/components/widgets/EmbeddableCalculator';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const Widget: React.FC = () => {
  const { widgetId } = useParams<{ widgetId: string }>();
  const [widget, setWidget] = useState<CalculatorWidget | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWidget = async () => {
      if (!widgetId) return;

      try {
        const { data, error } = await supabase
          .from('calculator_widgets')
          .select('*, organization_branding:organizations(organization_branding(*))')
          .eq('id', widgetId)
          .eq('is_active', true)
          .single();

        if (error) throw error;
        setWidget(data as CalculatorWidget);
      } catch (error) {
        console.error('Error fetching widget:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWidget();
  }, [widgetId]);

  const handleLeadSubmit = async (
    leadData: { firstName: string; lastName: string; email: string; phone?: string },
    calculationData: any
  ) => {
    if (!widget || !widgetId) return;

    const { error } = await supabase.from('widget_leads').insert({
      widget_id: widgetId,
      organization_id: widget.organization_id,
      first_name: leadData.firstName || null,
      last_name: leadData.lastName || null,
      email: leadData.email,
      phone: leadData.phone || null,
      calculated_buying_power: calculationData.homePrice,
      calculator_inputs: {
        ...calculationData,
        savedAt: new Date().toISOString()
      },
      source_url: window.location.href
    });

    if (error) throw error;

    // Send notification email (fire and forget)
    supabase.functions.invoke('notify-new-lead', {
      body: {
        firstName: leadData.firstName,
        lastName: leadData.lastName,
        email: leadData.email,
        phone: leadData.phone,
        homePrice: calculationData.homePrice,
        monthlyPayment: calculationData.monthlyPayment,
        downPayment: calculationData.downPayment,
        interestRate: calculationData.interestRate,
        widgetId: widgetId,
        organizationId: widget.organization_id,
        sourceUrl: window.location.href,
        source: 'widget'
      }
    }).catch(err => console.error('Failed to send lead notification:', err));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!widget) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Widget Not Found</h2>
            <p className="text-muted-foreground">
              This calculator widget is not available or has been disabled.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Extract branding from the nested structure
  const branding = (widget as any).organization_branding?.[0]?.organization_branding?.[0];
  const primaryColor = widget.custom_styles?.primary_color || branding?.primary_color || '#2563eb';
  const companyName = branding?.company_name || 'Mortgage Calculator';
  const logoUrl = widget.custom_styles?.logo_url || branding?.logo_url;

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        background: `linear-gradient(135deg, ${primaryColor}10, ${primaryColor}05)`
      }}
    >
      <div className="container mx-auto max-w-lg">
        <EmbeddableCalculator
          primaryColor={primaryColor}
          companyName={companyName}
          logoUrl={logoUrl}
          leadCaptureEnabled={widget.lead_capture_enabled}
          onLeadSubmit={handleLeadSubmit}
        />
      </div>
    </div>
  );
};

export default Widget;
